import {
  Component,
  DestroyRef,
  HostListener,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { AuthService } from '../../services/auth.service';
import { HeaderNavService } from '../../services/header-nav.service';
import { UserService } from '../../services/user.service';
import { AuthClaimsService } from '../../services/auth-claims.service';
import { NavigationService } from '../../services/navigation.service';
import { TestBannerComponent } from '../test-banner/test-banner.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [RouterLink, LucideAngularModule, TestBannerComponent],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './header.component.html',
})
export class HeaderComponent {
  readonly brandLogoSrc = 'assets/logo.svg';

  private readonly authService = inject(AuthService);
  private readonly headerNav = inject(HeaderNavService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly userService = inject(UserService);
  readonly navService = inject(NavigationService);
  readonly authClaims = inject(AuthClaimsService);

  readonly searchQuery = signal('');
  readonly profileOpen = signal(false);
  readonly mobileOpen = signal(false);

  readonly authUser = toSignal(this.authService.currentUser$, { initialValue: null });

  readonly isLoggedIn = computed(() => {
    const u = this.authUser();
    return !!u && u.emailVerified;
  });

  readonly isOwner = this.userService.isOwner;
  readonly isAdmin = this.authClaims.isAdmin;

  readonly userInitials = computed(() => {
    const u = this.authUser();
    const name = u?.displayName?.trim() || u?.email?.trim() || '';
    if (!name) return '?';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  });

  readonly createLabel = computed(() =>
    this.isLoggedIn() ? '+ Crear espacio' : '+ Añadir espacio',
  );

  constructor() {
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.closeOverlays());
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeOverlays();
  }

  closeOverlays(): void {
    this.profileOpen.set(false);
    this.mobileOpen.set(false);
  }

  toggleProfile(): void {
    this.mobileOpen.set(false);
    this.profileOpen.update(v => !v);
  }

  toggleMobile(): void {
    this.profileOpen.set(false);
    this.mobileOpen.update(v => !v);
  }

  onSearchSubmit(event: Event): void {
    event.preventDefault();
    this.headerNav.goToExplore(this.authUser(), this.searchQuery());
    this.closeOverlays();
  }

  async onCreate(): Promise<void> {
    await this.headerNav.goToCreate(this.authUser());
    this.closeOverlays();
  }

  onLogin(): void {
    void this.router.navigate(['/login']);
    this.closeOverlays();
  }

  goToExplore(): void {
    this.headerNav.goToExplore(this.authUser());
    this.closeOverlays();
  }

  goToMySpaces(): void {
    this.navService.setView('mis-espacios');
    this.headerNav.goToMySpaces();
    this.closeOverlays();
  }

  goToAccount(): void {
    this.headerNav.goToAccount();
    this.closeOverlays();
  }

  goToAdmin(): void {
    this.headerNav.goToAdmin();
    this.closeOverlays();
  }

  logout(): void {
    this.closeOverlays();
    void this.authService.signOut();
  }
}
